import styles from './Forms.module.css';

import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Highlighter from 'react-highlight-words';
import { Link } from 'react-router-dom';
import { withRouter } from 'react-router';
import { PageHeader, Card, Spin, Button, Tooltip, Table, Input, Typography } from 'antd';

import {
  ReloadOutlined,
  SearchOutlined,
  LinkOutlined,
  UserOutlined,
  ProfileOutlined
} from '@ant-design/icons';

import {
  getForms, clearForms
} from 'store/ducks/forms.js';

const Forms = ({ history }) => {
  const dispatch = useDispatch();

  const forms = useSelector(state => state.forms.forms);
  const loading = useSelector(state => state.forms.getForms.loading);

  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const [pageSize, setPageSize] = useState(20);

  const reload = () => dispatch(getForms());

  const searchInput = React.createRef();

  useEffect(() => {
    dispatch(getForms());

    return () => {
      dispatch(clearForms());
    };
  }, []);

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();

    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = clearFilters => {
    clearFilters();
    setSearchText('')
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }} />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon={<SearchOutlined />}
          size="small"
          style={{ width: 90, marginRight: 8 }}>Search
        </Button>
        <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    ),
    filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) => record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : null,
    onFilterDropdownVisibleChange: visible => {
      if (visible) setTimeout(() => searchInput.current.select());
    },
    render: (text) => text ? (searchedColumn === dataIndex ? <Typography.Paragraph style={{ margin: 0 }} copyable={{ text }}>
      <Highlighter highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
        searchWords={[searchText]}
        autoEscape
        textToHighlight={text.toString()} />
    </Typography.Paragraph> : <Typography.Paragraph style={{ margin: 0 }} copyable>{text}</Typography.Paragraph>) : null
  });

  const pagination = {
    total: forms ? forms.length : 0,
    pageSize: pageSize,
    showSizeChanger: true,
    pageSizeOptions: [20, 40, 80, 100],
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
    onShowSizeChange: (current, pageSize) => setPageSize(pageSize)
  };

  const columns = [{
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    width: 300,
    ...getColumnSearchProps('name'),
  }, {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    sorter: (a, b) => a.type.length - b.type.length,
    filters: [{
      text: 'classic',
      value: 'classic',
    }, {
      text: 'conversational',
      value: 'conversational',
    },],
    onFilter: (value, record) => record && record.type.indexOf(value) === 0,
    filterMultiple: false
  }, {
    title: 'Submissions',
    dataIndex: 'submissions',
    key: 'submissions',
    sorter: (a, b) => a.submissions - b.submissions
  }, {
    title: 'Owner',
    dataIndex: 'ownerEmail',
    key: 'ownerEmail',
    ...getColumnSearchProps('ownerEmail')
  }, {
    title: '',
    dataIndex: 'action',
    key: 'action',
    align: 'right',
    width: 100,
    render: (text, row, index) => <>
      {row.owner && <Tooltip placement="top" title="Form Preview" arrowPointAtCenter>
        <Link to={`/users/${row.owner._id}`} style={{ margin: '0 5px 0 0' }}><UserOutlined /></Link>
      </Tooltip>}

      <Tooltip placement="top" title="Open Form" arrowPointAtCenter>
        <a href={`https://form.questionscout.com/${row._id}-::${row.previewToken}?qs__ghost=true`} rel="noopener noreferrer" target="_blank" style={{ margin: '0 20px 0 0' }}><LinkOutlined /></a>
      </Tooltip>

      <Tooltip placement="top" title="Form Details" arrowPointAtCenter>
        <Link to={`/forms/${row._id}`}><ProfileOutlined /></Link>
      </Tooltip>
    </>
  }];

  return (
    <>
      <PageHeader
        title="Forms"
        style={{ border: '1px solid #f0f0f0', background: '#fff', margin: '0 0 15px 0' }}
        extra={[
          <Button key="reload" onClick={() => reload()}><ReloadOutlined /></Button>
        ]}
      />

      <Spin spinning={loading} size="large">
        <Card style={{ width: '100%', minHeight: 224, padding: '40px 0 0 0' }}>
          {forms && !loading && <Table rowKey="_id" dataSource={forms} columns={columns} pagination={pagination} onRow={(record) => {
            return {
              onDoubleClick: () => history.push({ pathname: `/forms/${record._id}` })
            };
          }} />}
        </Card>
      </Spin>
    </>
  );
}

export default withRouter(Forms);
